import Result from "@/core/common/Result";
import { AbstractCSAT } from "../AbstractCSAT";
import { inject, injectable } from "inversify";
import { SalvarCSATMinhasEmpresasUseCaseAdapter } from "../adapters/SalvarCSATMinhasEmpresasAdapter";
import { type ISalvarCSATMinhasEmpresasUseCase } from "../ports/SalvarCSATMinhasEmpresasUseCase";
import "reflect-metadata";
import { ISalvarCSATMinhasEmpresasHandler } from "../ports/SalvarCSATMinhasEmpresasHandler";

export type MinhasEmpresasState = {
  nota: number | undefined;
  descricao: string;
};

export enum CSATMinhasEmpresasPaginas {
  nota = 1,
  comentario = 2,
  enviado = 3
}

@injectable()
export class MinhasEmpresasHandler
  extends AbstractCSAT<MinhasEmpresasState>
  implements ISalvarCSATMinhasEmpresasHandler
{
  @inject(SalvarCSATMinhasEmpresasUseCaseAdapter)
  private readonly useCase!: ISalvarCSATMinhasEmpresasUseCase;

  private idPesquisa: string | null = null;

  state: MinhasEmpresasState = {
    descricao: "",
    nota: undefined
  };

  constructor() {
    super();

    super.setMaxPage(3);
  }

  async podeVisualizarPesquisa(): Promise<boolean> {
    const response = await this.podeVisualizarPesquisaUseCase.podeVisualizar({
      pesquisa: "minhas-empresas"
    });

    if (response.isFailure) return false;

    this.idPesquisa = response.getValue().id;

    return response.getValue().podeVisualizar;
  }

  private notaFoiSelecionada(): this is { state: { nota: number } } {
    return typeof this.state.nota === "number";
  }

  async sendCSAT(): Promise<Result<void>> {
    if (!this.notaFoiSelecionada())
      return Result.fail("Por favor, selecione uma nota");

    if (!this.idPesquisa)
      return Result.fail("Ocorreu um erro ao salvar a pesquisa");

    const response = await this.useCase.execute({
      id: this.idPesquisa,
      comentario: this.state.descricao,
      nota: this.state.nota
    });

    if (response.isFailure) return Result.fail(response.error);

    return Result.ok();
  }

  private possuiNotaECSATFoiFechado(): boolean {
    return (
      this.getCurrentPage() != CSATMinhasEmpresasPaginas.enviado &&
      this.notaFoiSelecionada()
    );
  }

  async salvarProgressoMinhasEmpresas(): Promise<Result<void>> {
    if (!this.idPesquisa)
      return Result.fail("Ocorreu um erro ao salvar a pesquisa");

    if (this.possuiNotaECSATFoiFechado()) return this.sendCSAT();

    if (!this.notaFoiSelecionada()) {
      return this.ignorarPesquisaUseCase.ignorarPesquisa({
        pesquisaId: this.idPesquisa
      });
    }

    return Result.fail("Nota já foi enviada");
  }
}
